import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetchPatientIntakes, usePatientIntakes } from '../state/data/patientIntake';
import {
  REFERRAL_INTAKE_TYPE, REFERRAL_RELATIONSHIP_LABEL, REFERRAL_CHANNEL_LABEL,
  summarizeReferral, splitReferrals, invitesSharedLabel,
} from '../logic/referral';
import { CHECKIN_RED_CHIPS, checkinChips, hasCheckinRedFlag, isCheckinIntakeType } from '../logic/checkin';

// Friendly labels for known intake_type values. New types fall back to the raw slug.
const INTAKE_TYPE_LABEL = {
  onboarding:        'Onboarding',
  onboarding_update: 'Onboarding update',
  quarterly_checkin: 'Quarterly check-in',
  symptom_check:     'Symptom check',
  monthly_checkin:   'Monthly check-in',
  weekly_checkin:    'Weekly check-in',
  experience_survey: 'Experience survey',
  enrollment_complementary_intake_regular: 'Complementary intake',
  referral:          'Referral',
};

// Field labels — maps snake_case keys → human labels.
const FIELD_LABELS = {
  diagnosed_conditions: 'Diagnosed conditions',
  other_conditions:     'Other conditions',
  current_medications:  'Self-reported medications',
  known_allergies:      'Allergies',
  exercise_frequency:   'Exercise',
  diet_description:     'Diet',
  smoking_status:       'Smoking',
  alcohol_use:          'Alcohol',
  top_priority:         'Top priority',
  specific_goal:        'Goal',
  motivations:          'Motivations',
  challenges:           'Barriers',

  // Complementary intake (enrollment_complementary_intake_regular)
  occupation_situation:  'Situation',
  occupation_career:     'Occupation',
  pharmacy_has:          'Has a pharmacy',
  pharmacy_name:         'Pharmacy',
  pharmacy_street:       'Street',
  pharmacy_city:         'City',
  pharmacy_state:        'State',
  hx_cardiovascular:     'Heart & blood vessels',
  hx_metabolic:          'BP, cholesterol & blood sugar',
  hx_other:              'Other conditions',
  bp_meds:               'BP medicines',
  bp_med_side_effects:   'BP med side effects',
  bp_stopped_reason:     'Reason BP med stopped',
  bp_recent_known:       'Knows recent BP',
  bp_recent_value:       'Recent BP reading',
  bp_er_6mo:             'ER for high BP (6mo)',
  bp_er_reason:          'ER reason',
  bp_history_note:       'BP history note',
  chol_meds:             'Cholesterol medicines',
  chol_statins_tried:    'Statins tried',
  chol_statin_symptoms:  'Statin symptoms',
  echo_recent:           'Recent echocardiogram',
  echo_ef:               'Ejection fraction',
  syncope:               'Fainting / blackouts',
  ckd:                   'Kidney disease',
  home_oxygen:           'Home oxygen',
  hyperthyroid_treated:  'Treated hyperthyroid',
  family_pkd:            'Family PKD',
  family_pkd_imaging:    'PKD imaging done',
  allergy_any:           'Has medicine allergies',
  allergy_entries:       'Allergies',
  otc_nsaid_freq:        'NSAID use',
  otc_cold:              'Cold / OTC medicines',
  supplements_any:       'Takes supplements',
  supplements_list:      'Supplements',
  tobacco_status:        'Smoking',
  tobacco_quit_interest: 'Interest in quitting',
  tobacco_quit_tried:    'Tried to quit',
  tobacco_quit_detail:   'Quit attempt detail',
  vape_status:           'Vaping',
  vape_nicotine:         'Vape nicotine',
  vape_frequency:        'Vape frequency',
  audit_freq:            'Alcohol frequency',
  audit_typical:         'Drinks per occasion',
  audit_binge:           'Binge frequency',
  substance_gate:        'Substance use',
  cannabis_use:          'Cannabis use',
  cannabis_type:         'Cannabis type',
  cannabis_symptoms:     'Cannabis symptoms',
  illicit_substances:    'Other substances',

  // Check-in, monthly and the retired weekly (the 1-5 items take their label from LIKERT_SCALES below)
  symptoms:              'Symptoms',
  note:                  'Note',

  // Experience survey (the 1-5 items take their label from EXPERIENCE_SCALES below)
  main_goal:             'Main reason for joining',
  main_goal_other:       'Other reason',
  visit_status:          'Virtual Visit',
  nps_0_10:              'Would recommend (0-10)',
  improve:               'One thing to improve',
  followup_ok:           'OK to follow up',
};

// The referral intake's own labels, scoped to that type so another form's `message` stays plain.
const REFERRAL_FIELD_LABELS = {
  relationship: 'Invited',
  channel:      'Shared via',
  message:      'Message prefilled',
};

// GUARDED COPY of the monthly (and retired weekly) check-in forms' INTAKE_CONFIG: `low`/`high` verbatim,
// `label`/`short` are ours. The patient saw only the two end words, and 1 is the worse end on every item,
// incl. the inverted-reading stress/device ones. Parity: scripts/check-intake-scale-parity.mjs.
// LIKERT-SCALES-START
const LIKERT_SCALES = {
  stress_1_5:       { label: 'Stress',         short: 'stress',   low: 'Very stressed', high: 'Not at all' },
  sleep_1_5:        { label: 'Sleep',          short: 'sleep',    low: 'Poorly',        high: 'Very well' },
  salt_1_5:         { label: 'Salt',           short: 'salt',     low: 'Not well',      high: 'Very well' },
  food_1_5:         { label: 'Meals healthy',  short: 'meals',    low: 'Mostly not',    high: 'Mostly yes' },
  activity_1_5:     { label: 'Activity',       short: 'activity', low: 'Barely',        high: 'Very active' },
  device_issue_1_5: { label: 'Device trouble', short: 'devices',  low: 'Lots',          high: 'None' },
};
// LIKERT-SCALES-END

// Experience survey 1-5 items — same guarded-copy contract as LIKERT_SCALES, against
// access-content/intakes/experience-survey.html. Kept apart because each map is compared
// key-for-key with its own form (check-intake-scale-parity.mjs). 1 is the worse end on every item.
// EXPERIENCE-SCALES-START
const EXPERIENCE_SCALES = {
  signup_clarity_1_5:     { label: 'Sign-up clarity',       short: 'sign-up',        low: 'Very unclear',     high: 'Very clear' },
  expectations_1_5:       { label: 'Understood the program', short: 'expectations',  low: 'Not at all',       high: 'Very well' },
  visit_booking_1_5:      { label: 'Visit booking',         short: 'booking',        low: 'Very difficult',   high: 'Very easy' },
  visit_quality_1_5:      { label: 'Virtual Visit',         short: 'visit',          low: 'Poor',             high: 'Excellent' },
  readings_ease_1_5:      { label: 'Taking readings',       short: 'readings',       low: 'Very difficult',   high: 'Very easy' },
  device_reliability_1_5: { label: 'Devices & app',         short: 'devices',        low: 'Lots of problems', high: 'No problems' },
  informed_1_5:           { label: 'Kept informed',         short: 'informed',       low: 'Not well',         high: 'Very well' },
  responsiveness_1_5:     { label: 'Care-team response',    short: 'response',       low: 'Poor',             high: 'Excellent' },
  pcp_confidence_1_5:     { label: 'PCP kept up to date',   short: 'PCP',            low: 'Not at all',       high: 'Very confident' },
  satisfaction_1_5:       { label: 'Overall satisfaction',  short: 'satisfaction',   low: 'Very dissatisfied', high: 'Very satisfied' },
};
// EXPERIENCE-SCALES-END

const scaleFor = (key) => LIKERT_SCALES[key] || EXPERIENCE_SCALES[key];

const EXPERIENCE_VALUE_LABEL = {
  main_goal: {
    manage_condition: 'Better manage my health condition', avoid_hospital: 'Avoid hospital or ER visits',
    support_between: 'More support between doctor visits', virtual_care: 'Convenience of virtual care',
    doctor_recommended: 'My doctor recommended it', other: 'Something else',
  },
  visit_status: { done: 'Had it', booked: 'Booked, not yet', not_booked: 'Not booked yet' },
  followup_ok: { yes: 'Yes', no: 'No' },
};

// Semantic risk levels for lifestyle enums. `null` = neutral (no color hint).
const RISK_MAP = {
  smoking_status:     { current: 'high', former: 'medium', never: 'low' },
  alcohol_use:        { heavy: 'high', moderate: 'medium', occasional: null, none: 'low' },
  exercise_frequency: { sedentary: 'high', '1-2x/wk': 'medium', '3-4x/wk': 'low', '5+x/wk': 'low' },
};

const RISK_COLOR = {
  high:   'var(--wi-status-bad,    #b3261e)',
  medium: 'var(--wi-status-warning,#a55c00)',
  low:    'var(--wi-status-good,   #0a8040)',
};

// Default section schema — covers the onboarding intake (the access-enrollment
// funnel submits these flat keys; see WF1 Build Onboarding Intake).
const FIELD_GROUPS = [
  { title: 'Goals',     fields: ['top_priority', 'specific_goal', 'motivations', 'challenges'] },
  { title: 'Lifestyle', fields: ['smoking_status', 'alcohol_use', 'exercise_frequency', 'diet_description'] },
  { title: 'Clinical',  fields: ['diagnosed_conditions', 'other_conditions', 'current_medications', 'known_allergies'] },
];

// Mirrors the check-in forms' three steps — the monthly form and the retired weekly one are identical.
const CHECKIN_SECTIONS = [
  { title: 'How they felt',   fields: ['stress_1_5', 'sleep_1_5'] },
  { title: 'Eating & moving', fields: ['salt_1_5', 'food_1_5', 'activity_1_5'] },
  { title: 'Flags & notes',   fields: ['symptoms', 'device_issue_1_5', 'note'] },
];

// Per-intake-type section schemas. Mirrors the spark form's step order; the
// submitted `data` stays flat (no nesting), this is presentation only. Keep in
// sync with the matching access-content intake form's INTAKE_CONFIG.steps.
const SECTION_SCHEMAS = {
  // Capture-only symptom check (WF9 pushes it on a BP crisis / Stage 2 / hypotension
  // reading). This IS the care team's reading surface for it — there is no push path.
  symptom_check: [
    { title: 'Symptoms',            fields: ['red_flags', 'red_flag_which', 'symptom_onset', 'other_symptoms'] },
    { title: 'About that reading',  fields: ['rested_5min', 'meds_taken_today'] },
    { title: 'Patient note',        fields: ['note'] },
  ],
  experience_survey: [
    { title: 'Getting started', fields: ['signup_clarity_1_5', 'expectations_1_5', 'main_goal', 'main_goal_other'] },
    { title: 'Virtual Visit',   fields: ['visit_status', 'visit_booking_1_5', 'visit_quality_1_5'] },
    { title: 'Readings & team', fields: ['readings_ease_1_5', 'device_reliability_1_5', 'informed_1_5', 'responsiveness_1_5', 'pcp_confidence_1_5'] },
    { title: 'Overall',         fields: ['nps_0_10', 'satisfaction_1_5', 'improve', 'followup_ok'] },
  ],
  monthly_checkin: CHECKIN_SECTIONS,
  weekly_checkin: CHECKIN_SECTIONS,
  enrollment_complementary_intake_regular: [
    { title: 'Day & work',                      fields: ['occupation_situation', 'occupation_career'] },
    { title: 'Pharmacy',                        fields: ['pharmacy_has', 'pharmacy_name', 'pharmacy_street', 'pharmacy_city', 'pharmacy_state'] },
    { title: 'Health history',                  fields: ['hx_cardiovascular', 'hx_metabolic', 'hx_other'] },
    { title: 'Blood-pressure medicines',        fields: ['bp_meds', 'bp_med_side_effects', 'bp_stopped_reason'] },
    { title: 'Blood pressure & recent control', fields: ['bp_recent_known', 'bp_recent_value', 'bp_er_6mo', 'bp_er_reason', 'bp_history_note'] },
    { title: 'Cholesterol',                     fields: ['chol_meds', 'chol_statins_tried', 'chol_statin_symptoms'] },
    { title: 'Heart, kidneys, lungs & thyroid', fields: ['echo_recent', 'echo_ef', 'syncope', 'ckd', 'home_oxygen', 'hyperthyroid_treated'] },
    { title: 'Family history',                  fields: ['family_pkd', 'family_pkd_imaging'] },
    { title: 'Medicine allergies & reactions',  fields: ['allergy_any', 'allergy_entries'] },
    { title: 'Everyday medicines & supplements', fields: ['otc_nsaid_freq', 'otc_cold', 'supplements_any', 'supplements_list'] },
    { title: 'Smoking & vaping',                fields: ['tobacco_status', 'tobacco_quit_interest', 'tobacco_quit_tried', 'tobacco_quit_detail', 'vape_status', 'vape_nicotine', 'vape_frequency'] },
    { title: 'Alcohol & substance use',         fields: ['audit_freq', 'audit_typical', 'audit_binge', 'substance_gate', 'cannabis_use', 'cannabis_type', 'cannabis_symptoms', 'illicit_substances'] },
  ],
  referral: [
    { title: 'Invite', fields: ['relationship', 'channel', 'message'] },
  ],
};

// Internal/transport keys never shown as patient answers (WF39 injects source_task_id).
const HIDDEN_KEYS = new Set(['source_task_id', 'form_version', 'copy_version', 'shared_at']);

const PAGE_SIZE = 50;

function formatLabel(key, intakeType) {
  return (intakeType === REFERRAL_INTAKE_TYPE && REFERRAL_FIELD_LABELS[key])
    || FIELD_LABELS[key]
    || scaleFor(key)?.label
    || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTypeLabel(type) {
  return INTAKE_TYPE_LABEL[type] || (type ? type.replace(/_/g, ' ') : '—');
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Patient-facing symptom labels, so the collapsed row reads as prose rather than slugs.
const SYMPTOM_LABEL = {
  chest_pain: 'chest pain',
  shortness_of_breath: 'shortness of breath',
  severe_headache: 'severe headache',
  vision_changes: 'vision changes',
  dizzy_or_faint: 'dizzy or faint',
  palpitations: 'racing heartbeat',
  swelling: 'new swelling',
  headache: 'headache',
  tiredness: 'tiredness',
  // check-in chips
  dizziness: 'dizziness',
  chest_tightness: 'chest tightness',
};

// The check-in's chip says just "Swelling" — only the symptom check asks about NEW swelling.
const CHECKIN_SYMPTOM_LABEL = { ...SYMPTOM_LABEL, swelling: 'swelling' };

// Keys whose array values come from SYMPTOM_LABEL's vocabulary.
const SYMPTOM_KEYS = new Set(['symptoms', 'other_symptoms', 'red_flag_which']);

function symptomLabel(v, key) {
  const labels = key === 'symptoms' ? CHECKIN_SYMPTOM_LABEL : SYMPTOM_LABEL;
  return labels[v] || String(v).replace(/_/g, ' ');
}

// A 1-5 answer, or null for anything that isn't one — an out-of-range or unparseable value
// falls through to the plain renderer rather than being drawn on a scale it doesn't belong to.
function likertScore(v) {
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : null;
}

// The symptom check is CAPTURE-ONLY — nothing pushes it anywhere, so this row is the only
// place a red-flag answer surfaces. It must be legible without expanding the row.
function summarizeSymptomCheck(data) {
  const which = Array.isArray(data.red_flag_which) ? data.red_flag_which : [];
  const named = which.map(v => SYMPTOM_LABEL[v] || String(v).replace(/_/g, ' ')).join(', ');
  if (data.red_flags === 'yes') return `Reported: ${named || 'red-flag symptoms'}`;
  const other = (Array.isArray(data.other_symptoms) ? data.other_symptoms : []).filter(v => v !== 'none');
  if (other.length) return `No red flags · ${other.map(v => SYMPTOM_LABEL[v] || v).join(', ')}`;
  if (data.red_flags === 'no') return 'No red-flag symptoms';
  return '';
}

// The check-in is the highest-volume intake, so without a summary every row reads identically and
// the only way to find a bad month is to open all of them. "Low" is 1-2 on a scale where 1 is
// always the worse end. Symptoms lead when present: they are the most clinically salient answer.
function summarizeCheckin(data) {
  const parts = [];
  // The module's clamp, as the ⚠ reads them: a warning never sits beside a symptom list that left its cause out.
  const reported = checkinChips(data.symptoms);
  const named = reported.filter(v => v !== 'none');
  // Red chips lead, so the row's warning sign sits next to the symptom that raised it.
  const red = named.filter(v => CHECKIN_RED_CHIPS.includes(v));
  const ordered = [...red, ...named.filter(v => !red.includes(v))];
  if (ordered.length) parts.push(ordered.map(v => symptomLabel(v, 'symptoms')).join(', '));

  const scored = Object.keys(LIKERT_SCALES).filter(k => likertScore(data[k]) !== null);
  if (scored.length) {
    const low = scored.filter(k => likertScore(data[k]) <= 2);
    parts.push(low.length
      ? `${low.length} of ${scored.length} low \u00B7 ${low.map(k => LIKERT_SCALES[k].short).join(', ')}`
      : 'No low scores');
  }
  if (!named.length && reported.includes('none')) parts.push('no symptoms');
  // Only that a note exists: what WF78 read in it shows on the task, and its words in the expanded row.
  if (typeof data.note === 'string' && data.note.trim()) parts.push('note');
  return parts.join(' \u00B7 ');
}

// Leads with the NPS answer, then any 1-2 answers, then whether they agreed to a follow-up call.
function summarizeExperienceSurvey(data) {
  const parts = [];
  const nps = Number(data.nps_0_10);
  if (String(data.nps_0_10 ?? '').trim() !== '' && Number.isInteger(nps) && nps >= 0 && nps <= 10) parts.push(`Recommend ${nps}/10`);
  const scored = Object.keys(EXPERIENCE_SCALES).filter(k => likertScore(data[k]) !== null);
  if (scored.length) {
    const low = scored.filter(k => likertScore(data[k]) <= 2);
    parts.push(low.length ? `low: ${low.map(k => EXPERIENCE_SCALES[k].short).join(', ')}` : 'No low scores');
  }
  if (data.followup_ok === 'yes') parts.push('OK to follow up');
  return parts.join(' \u00B7 ');
}

function summarizeData(data, intakeType) {
  if (!data || typeof data !== 'object') return '';
  if (intakeType === 'symptom_check') return summarizeSymptomCheck(data);
  if (isCheckinIntakeType(intakeType)) return summarizeCheckin(data);
  if (intakeType === 'experience_survey') return summarizeExperienceSurvey(data);
  if (intakeType === REFERRAL_INTAKE_TYPE) return summarizeReferral(data);
  if (data.top_priority)  return String(data.top_priority);
  if (data.specific_goal) return String(data.specific_goal);
  return '';
}

// A red-flag answer must not read as ordinary text. A check-in's ⚠ comes from its chips; a red WF78 finds in the note shows on its task.
export function isUrgentIntakeSummary(data, intakeType) {
  if (!data) return false;
  if (intakeType === 'symptom_check') return data.red_flags === 'yes';
  return isCheckinIntakeType(intakeType) && hasCheckinRedFlag(data);
}

// Some onboarding rows store array fields double-encoded as JSON strings inside
// the JSONB `data` column (e.g. `challenges: "[\"a\",\"b\"]"`). Defensively
// unwrap top-level string values that parse as JSON arrays so the rest of the
// renderer treats them as proper arrays. Strings that don't parse, or parse to
// non-arrays, are left untouched.
function maybeParseEncodedArray(v) {
  if (typeof v !== 'string') return v;
  if (v.length < 2 || v.trimStart()[0] !== '[') return v;
  try {
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed : v;
  } catch {
    return v;
  }
}

// Normalize an intake row from either the bundle (camelCase-ish) or WF37 (snake_case).
export function normalizeIntake(raw) {
  if (!raw) return null;
  const data = typeof raw.data === 'string'
    ? (() => { try { return JSON.parse(raw.data); } catch { return {}; } })()
    : (raw.data || {});
  const fixedData = {};
  for (const [k, v] of Object.entries(data)) fixedData[k] = maybeParseEncodedArray(v);
  return {
    intake_id:    raw.intake_id || raw.id,
    intake_type:  raw.intake_type,
    submitted_at: raw.submitted_at,
    data:         fixedData,
  };
}

// Generic list of patient-submitted intake forms. Collapsed (default) shows the
// 3 most recent from the bundle; expanded fetches the full filtered list.
export default function PatientIntakesList({ intakes, totalCount, withingsUserId }) {
  const seed = useMemo(
    () => (Array.isArray(intakes) ? intakes : []).map(normalizeIntake).filter(Boolean),
    [intakes],
  );
  const count = Number.isFinite(totalCount) ? totalCount : seed.length;

  // A referral in the bundle's three newest rows would crowd out a clinical intake, so read a
  // wider page (holds while fewer than 18 of the newest 20 are referrals) and fold referrals.
  const seedHasReferral = seed.some(i => i.intake_type === REFERRAL_INTAKE_TYPE);
  const moreOnFile = count > seed.length;
  const wider = usePatientIntakes(seedHasReferral && moreOnFile ? withingsUserId : null, { limit: 20 });
  const invites = usePatientIntakes(seedHasReferral || moreOnFile ? withingsUserId : null, { intakeType: REFERRAL_INTAKE_TYPE, limit: 1 });
  const widerOk = wider.data?.status === 'success' && Array.isArray(wider.data.data);
  const pool = useMemo(
    () => (widerOk ? wider.data.data.map(normalizeIntake).filter(Boolean) : seed),
    [widerOk, wider.data, seed],
  );
  const widerPending = seedHasReferral && moreOnFile && !widerOk;
  const { shown, referrals } = splitReferrals(pool);
  const inviteCount = Number(invites.data?.meta?.count);
  const referralTotal = Number.isFinite(inviteCount) && inviteCount > 0 ? inviteCount : referrals.length;
  const latestReferral = normalizeIntake(invites.data?.data?.[0]) || referrals[0];
  const hiddenClinical = Math.max(0, count - referralTotal - shown.length);

  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div style={{
        margin: '24px 0 10px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span className="eyebrow text-secondary">Patient Intakes</span>
        {count > 0 && (
          <span className="detail1 text-secondary" style={{ fontSize: 11 }}>
            {count} on file
          </span>
        )}
      </div>

      {count === 0 ? (
        <EmptyState />
      ) : expanded ? (
        <IntakesListView
          withingsUserId={withingsUserId}
          onCollapse={() => setExpanded(false)}
        />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {shown.map(it => <IntakeRow key={it.intake_id} intake={it} />)}
            {widerPending && (
              <p className="detail1 text-secondary" style={{ margin: '4px 2px' }}>
                {wider.isError ? 'Couldn’t load the latest intakes — use Show all.' : 'Loading the latest intakes…'}
              </p>
            )}
            {referralTotal > 0 && <ReferralLine count={referralTotal} latest={latestReferral} />}
          </div>
          {(hiddenClinical > 0 || referralTotal > 0 || widerPending) && withingsUserId && (
            <ShowAllButton
              hiddenCount={hiddenClinical}
              onClick={() => setExpanded(true)}
            />
          )}
        </>
      )}
    </>
  );
}

// Every referral folded into one line — an invite count, not a conversion count.
function ReferralLine({ count, latest }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
      background: 'var(--wi-background-secondary)', borderRadius: 12,
      border: '1px solid var(--wi-divider)',
    }}>
      <span className="detail1 text-secondary" style={{
        fontSize: 11, minWidth: 92, fontVariantNumeric: 'tabular-nums',
      }}>
        {formatDate(latest?.submitted_at)}
      </span>
      <span style={{
        fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
        textTransform: 'uppercase', color: 'var(--wi-text-secondary)', minWidth: 96,
      }}>
        {formatTypeLabel(REFERRAL_INTAKE_TYPE)}
      </span>
      <span className="body2" style={{ flex: 1, color: 'var(--wi-text-primary)' }}>
        {invitesSharedLabel(count)}
      </span>
      <span className="detail1 text-secondary" style={{ fontSize: 11 }}>Tracking only</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      background: 'var(--wi-background-secondary)', borderRadius: 12,
      padding: '18px 20px', border: '1px dashed var(--wi-divider)',
    }}>
      <p className="detail1 text-secondary" style={{ margin: 0 }}>
        No intakes on file for this patient.
      </p>
    </div>
  );
}

function ShowAllButton({ hiddenCount, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        width: '100%', marginTop: 6, padding: '10px 14px',
        background: 'var(--wi-background-secondary)',
        border: '1px dashed var(--wi-divider)', borderRadius: 8,
        cursor: 'pointer', fontFamily: 'var(--wi-font-family)',
        fontSize: 13, fontWeight: 500, color: 'var(--wi-action-primary)',
      }}
    >
      <span style={{ display: 'inline-flex', transition: 'transform 200ms ease' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </span>
      <span>{hiddenCount > 0 ? `Show all intakes (${hiddenCount} more)` : 'Show all intakes'}</span>
    </button>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Expanded list view — filters + pagination via WF37                       */
/* ──────────────────────────────────────────────────────────────────────── */

function IntakesListView({ withingsUserId, onCollapse }) {
  const [typeFilter, setTypeFilter]   = useState('');
  const [fromDate, setFromDate]       = useState('');
  const [toDate, setToDate]           = useState('');
  const [rows, setRows]               = useState([]);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [offset, setOffset]           = useState(0);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const reqIdRef = useRef(0);

  // Convert a YYYY-MM-DD date input to an ISO-8601 instant. "From" is start of
  // day UTC; "To" is end of day UTC, so the bounds are inclusive of full days.
  const toIsoBound = (value, boundary) => {
    if (!value) return undefined;
    return boundary === 'start'
      ? `${value}T00:00:00Z`
      : `${value}T23:59:59Z`;
  };

  const fetchPatientIntakes = useFetchPatientIntakes();

  const fetchPage = useCallback(async (nextOffset, { append }) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPatientIntakes(withingsUserId, {
        intakeType: typeFilter || undefined,
        submittedAfter:  toIsoBound(fromDate, 'start'),
        submittedBefore: toIsoBound(toDate,   'end'),
        offset: nextOffset,
        limit: PAGE_SIZE,
      });
      if (reqId !== reqIdRef.current) return; // a newer request has superseded this one
      if (!result || result.status !== 'success') {
        setError(result?.error?.message || 'Failed to load intakes');
        return;
      }
      const page = Array.isArray(result.data) ? result.data.map(normalizeIntake).filter(Boolean) : [];
      setRows(prev => append ? prev.concat(page) : page);
      setTotalFiltered(Number(result.meta?.count) || 0);
      setOffset(nextOffset + page.length);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err?.message || 'Failed to load intakes');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [withingsUserId, typeFilter, fromDate, toDate, fetchPatientIntakes]);

  // Reload from scratch when any filter changes. Debounce so date typing doesn't thrash.
  useEffect(() => {
    const timer = setTimeout(() => { fetchPage(0, { append: false }); }, 250);
    return () => clearTimeout(timer);
  }, [fetchPage]);

  const hasMore = offset < totalFiltered;

  const clearFilters = () => {
    setTypeFilter('');
    setFromDate('');
    setToDate('');
  };

  const filtersActive = typeFilter || fromDate || toDate;

  return (
    <div style={{
      background: 'var(--wi-background-secondary)', borderRadius: 12,
      border: '1px solid var(--wi-divider)', padding: 12,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <FilterBar
        typeFilter={typeFilter} setTypeFilter={setTypeFilter}
        fromDate={fromDate} setFromDate={setFromDate}
        toDate={toDate} setToDate={setToDate}
        onClear={clearFilters}
        onCollapse={onCollapse}
        filtersActive={!!filtersActive}
        total={totalFiltered}
        loading={loading}
      />

      {error && (
        <div style={{
          padding: '10px 12px', borderRadius: 8,
          background: 'var(--wi-status-bad-bg, rgba(179,38,30,0.08))',
          color: 'var(--wi-status-bad, #b3261e)', fontSize: 12,
        }}>
          {error}
        </div>
      )}

      {!error && rows.length === 0 && !loading && (
        <div style={{
          padding: '18px 20px', borderRadius: 12,
          border: '1px dashed var(--wi-divider)',
        }}>
          <p className="detail1 text-secondary" style={{ margin: 0 }}>
            No intakes match these filters.{' '}
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: 'var(--wi-action-primary)', cursor: 'pointer',
                  fontFamily: 'var(--wi-font-family)', fontSize: 'inherit',
                }}
              >
                Clear filters
              </button>
            )}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(it => <IntakeRow key={it.intake_id} intake={it} />)}
        </div>
      )}

      {hasMore && !error && (
        <button
          type="button"
          onClick={() => fetchPage(offset, { append: true })}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', padding: '10px 14px',
            background: 'var(--wi-background-primary)',
            border: '1px dashed var(--wi-divider)', borderRadius: 8,
            cursor: loading ? 'wait' : 'pointer',
            fontFamily: 'var(--wi-font-family)',
            fontSize: 13, fontWeight: 500, color: 'var(--wi-action-primary)',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Loading…' : `Load more (${totalFiltered - offset} remaining)`}
        </button>
      )}
    </div>
  );
}

function FilterBar({
  typeFilter, setTypeFilter,
  fromDate, setFromDate,
  toDate, setToDate,
  onClear, onCollapse, filtersActive, total, loading,
}) {
  const typeOptions = Object.keys(INTAKE_TYPE_LABEL);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      }}>
        <button
          type="button"
          onClick={onCollapse}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
            color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)',
            fontSize: 12, fontWeight: 500,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to recent
        </button>
        <span className="detail1 text-secondary" style={{ fontSize: 11 }}>
          {loading ? 'Loading…' : `${total} ${total === 1 ? 'intake' : 'intakes'}`}
        </span>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <span className="text-secondary">Type</span>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: 6,
              border: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-primary)',
              color: 'var(--wi-text-primary)',
              fontFamily: 'var(--wi-font-family)', fontSize: 12,
            }}
          >
            <option value="">All types</option>
            {typeOptions.map(t => (
              <option key={t} value={t}>{INTAKE_TYPE_LABEL[t]}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <span className="text-secondary">From</span>
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={e => setFromDate(e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: 6,
              border: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-primary)',
              color: 'var(--wi-text-primary)',
              fontFamily: 'var(--wi-font-family)', fontSize: 12,
            }}
          />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <span className="text-secondary">To</span>
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={e => setToDate(e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: 6,
              border: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-primary)',
              color: 'var(--wi-text-primary)',
              fontFamily: 'var(--wi-font-family)', fontSize: 12,
            }}
          />
        </label>
        {filtersActive && (
          <button
            type="button"
            onClick={onClear}
            style={{
              background: 'none', border: 'none', padding: '4px 6px', cursor: 'pointer',
              color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)',
              fontSize: 12, fontWeight: 500,
            }}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Row + body + renderers (unchanged behavior, small tidy-ups)              */
/* ──────────────────────────────────────────────────────────────────────── */

function IntakeRow({ intake }) {
  const [rowExpanded, setRowExpanded] = useState(false);
  const typeLabel = formatTypeLabel(intake.intake_type);
  const summary = summarizeData(intake.data, intake.intake_type);
  const urgent = isUrgentIntakeSummary(intake.data, intake.intake_type);

  return (
    <div style={{
      background: 'var(--wi-background-secondary)', borderRadius: 12,
      border: '1px solid var(--wi-divider)', overflow: 'hidden',
    }}>
      <button
        onClick={() => setRowExpanded(v => !v)}
        aria-expanded={rowExpanded}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, width: '100%',
          padding: '12px 16px', background: 'transparent', border: 'none',
          textAlign: 'left', cursor: 'pointer', color: 'var(--wi-text-primary)',
        }}
      >
        <span className="detail1 text-secondary" style={{
          fontSize: 11, minWidth: 92, fontVariantNumeric: 'tabular-nums',
        }}>
          {formatDate(intake.submitted_at)}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
          textTransform: 'uppercase', color: 'var(--wi-text-secondary)',
          minWidth: 96,
        }}>
          {typeLabel}
        </span>
        {summary && (
          <span className="body2" style={{
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: urgent ? 'var(--wi-text-primary-bad)' : 'var(--wi-text-primary)',
            fontWeight: urgent ? 600 : 400,
          }}>
            {urgent ? '\u26A0 ' : ''}{summary}
          </span>
        )}
        <span style={{
          marginLeft: 'auto', fontSize: 12, color: 'var(--wi-text-secondary)',
          transition: 'transform 120ms ease',
          transform: rowExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          display: 'inline-block',
        }}>
          ›
        </span>
      </button>
      {rowExpanded && (
        <div style={{ padding: '4px 16px 16px' }}>
          <IntakeBody data={intake.data} intakeType={intake.intake_type} />
        </div>
      )}
    </div>
  );
}

export function IntakeBody({ data, intakeType }) {
  if (!data || typeof data !== 'object') {
    return <p className="detail1 text-secondary" style={{ margin: '12px 0 0' }}>No data captured.</p>;
  }

  const nonEmpty = (k) => {
    const v = data[k];
    return !(v == null || v === '' || (Array.isArray(v) && v.length === 0));
  };

  const groups = SECTION_SCHEMAS[intakeType] || FIELD_GROUPS;
  const groupedKeys = new Set(groups.flatMap(g => g.fields));
  // Any non-empty key the schema doesn't place still surfaces under "Other" —
  // so a new form field is never silently hidden.
  const otherKeys = Object.keys(data).filter(
    k => !groupedKeys.has(k) && !HIDDEN_KEYS.has(k) && nonEmpty(k),
  );

  const visibleGroups = groups
    .map(g => ({ ...g, fields: g.fields.filter(f => nonEmpty(f) && data[f] !== undefined) }))
    .filter(g => g.fields.length > 0);

  if (visibleGroups.length === 0 && otherKeys.length === 0) {
    return <p className="detail1 text-secondary" style={{ margin: '12px 0 0' }}>No data captured.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 12 }}>
      {intakeType === REFERRAL_INTAKE_TYPE && (
        <p className="detail1 text-secondary" style={{ margin: 0 }}>
          Tracking only — Withings never contacts invitees.
        </p>
      )}
      {visibleGroups.map(g => (
        <Section key={g.title} title={g.title}>
          {g.fields.map(f => <Row key={f} fieldKey={f} value={data[f]} intakeType={intakeType} />)}
        </Section>
      ))}
      {otherKeys.length > 0 && (
        <Section title="Other">
          {otherKeys.map(k => <Row key={k} fieldKey={k} value={data[k]} intakeType={intakeType} />)}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 600, letterSpacing: 0.6,
        textTransform: 'uppercase', color: 'var(--wi-text-secondary)',
        marginBottom: 8, paddingBottom: 6,
        borderBottom: '1px solid var(--wi-divider)',
      }}>
        {title}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(140px, 200px) 1fr',
        columnGap: 16, rowGap: 8,
      }}>
        {children}
      </div>
    </div>
  );
}

function Row({ fieldKey, value, intakeType }) {
  return (
    <>
      <div className="detail1 text-secondary" style={{
        fontSize: 12, paddingTop: 1,
      }}>
        {formatLabel(fieldKey, intakeType)}
      </div>
      <div className="body2" style={{ color: 'var(--wi-text-primary)' }}>
        {renderValue(fieldKey, value, intakeType)}
      </div>
    </>
  );
}

// A 1-5 answer rendered as the position on its own anchored scale. The number alone is
// unreadable (2 out of 5 on WHAT?) and reads backwards on the two items whose question is
// phrased in the opposite direction to the scale, so the anchors ship with every value.
// Fills only — never a --wi-status-* as text (see .claude/rules/app.md "Styling").
function ScaleMeter({ scale, score }) {
  const fill = score <= 2 ? 'var(--wi-status-moderate)'
    : score >= 4 ? 'var(--wi-status-good)'
      : 'var(--wi-text-secondary)';
  const readout = score <= 2
    ? {
      color: 'var(--wi-text-primary-moderate)', background: 'var(--wi-background-moderate)',
      padding: '1px 8px', borderRadius: 20,
    }
    : { color: 'var(--wi-text-primary)' };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
      <div
        role="img"
        aria-label={`${score} out of 5, on a scale from "${scale.low}" to "${scale.high}"`}
        style={{ flex: '1 1 auto', minWidth: 88, maxWidth: 260 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3, height: 8 }}>
          {[1, 2, 3, 4, 5].map(n => (
            <span key={n} style={{
              borderRadius: 2,
              background: n <= score ? fill : 'var(--wi-divider)',
            }} />
          ))}
        </div>
        <div className="text-secondary" style={{
          display: 'flex', justifyContent: 'space-between', gap: 10,
          fontSize: 11, lineHeight: 1.2, marginTop: 4,
        }}>
          <span>{scale.low}</span>
          <span style={{ textAlign: 'right' }}>{scale.high}</span>
        </div>
      </div>
      <span style={{
        flexShrink: 0, fontVariantNumeric: 'tabular-nums', fontSize: 12.5,
        fontWeight: 600, lineHeight: 1.35, ...readout,
      }}>
        {score}<span className="text-secondary" style={{ fontWeight: 400, fontSize: 11 }}>/5</span>
      </span>
    </div>
  );
}

function renderValue(key, v, intakeType) {
  if (v == null || v === '') {
    return <span className="text-secondary">—</span>;
  }
  // A 1-5 item: show where the answer sits between the words the patient actually saw.
  const scale = scaleFor(key);
  if (scale) {
    const score = likertScore(v);
    if (score !== null) return <ScaleMeter scale={scale} score={score} />;
  }
  // Symptom chips — patient-facing names, and "none" said plainly rather than as a slug.
  if (SYMPTOM_KEYS.has(key) && Array.isArray(v)) {
    const named = v.filter(x => x !== 'none');
    if (!named.length) {
      return <span className="text-secondary">{v.includes('none') ? 'None reported' : '\u2014'}</span>;
    }
    return <span>{named.map(x => symptomLabel(x, key)).join(', ')}</span>;
  }
  // allergy_entries is a repeatable group: [{ medication, reactions[] }].
  if (key === 'allergy_entries' && Array.isArray(v)) {
    if (v.length === 0) return <span className="text-secondary">—</span>;
    return (
      <span>
        {v.map((e, i) => {
          const name = e && e.medication ? String(e.medication) : '—';
          const reactions = Array.isArray(e?.reactions) ? e.reactions.join(', ') : '';
          return (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              <span style={{ fontWeight: 600 }}>{name}</span>
              {reactions && <span className="text-secondary"> — {reactions}</span>}
            </React.Fragment>
          );
        })}
      </span>
    );
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-secondary">—</span>;
    return (
      <span>
        {v.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-secondary">, </span>}
            {typeof item === 'object' ? JSON.stringify(item) : String(item).replace(/_/g, ' ')}
          </React.Fragment>
        ))}
      </span>
    );
  }
  if (typeof v === 'object') {
    return (
      <pre style={{
        background: 'var(--wi-background-primary)', padding: 8, borderRadius: 6,
        fontSize: 11, margin: 0, overflow: 'auto',
      }}>{JSON.stringify(v, null, 2)}</pre>
    );
  }
  const referral = intakeType === REFERRAL_INTAKE_TYPE;
  const choice = EXPERIENCE_VALUE_LABEL[key]?.[v]
    || (referral && key === 'relationship' ? REFERRAL_RELATIONSHIP_LABEL[v] : null)
    || (referral && key === 'channel' ? REFERRAL_CHANNEL_LABEL[v] : null);
  if (choice) return <span>{choice}</span>;
  // The prefilled share text: plain text in a quote, its link left as text, never an <a>.
  if (referral && key === 'message' && typeof v === 'string') {
    return (
      <blockquote style={{
        margin: 0, padding: '8px 12px', borderLeft: '3px solid var(--wi-divider)',
        color: 'var(--wi-text-secondary)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      }}>
        {v}
      </blockquote>
    );
  }
  const risk = RISK_MAP[key]?.[v];
  if (risk) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: RISK_COLOR[risk], flexShrink: 0,
        }} />
        {String(v)}
      </span>
    );
  }
  return <span>{String(v)}</span>;
}
